/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';
import { AccountService } from 'app/core';

import {
  GanttComponent,
  GanttModule,
  PdfExportProperties,
  RecordDoubleClickEventArgs,
  RowSelectEventArgs,
  TimelineSettingsModel,
  ToolbarItem,
  ToolbarService,
  PdfExportService,
  ExcelExportService,
  SelectionService,
  DayMarkersService,
  ResizeService,
} from '@syncfusion/ej2-angular-gantt';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Task } from '@tasks/tasks.types';
import { LegacyTasksTabService } from '../../../legacy-tasks/services/tasks.service';
import { GanttElementCssClass, GanttTaskData } from './gantt-chart-view.model';
import moment from 'moment';
import { GanttPdfExportComponent } from '../../../legacy-tasks/gantt-pdf-export/gantt-pdf-export.component';
import { Store } from '@ngxs/store';
import { TaskState } from '@tasks/state/state';
import { TaskUtils } from '@tasks/tasks.utils';
import { TaskFilterPipe } from '@shared/pipes/task-filter.pipe';
import { FeatureFlag } from '@core/enums';

@Component({
  selector: 'gantt-chart-view',
  templateUrl: './gantt-chart-view.component.html',
  styleUrls: ['./gantt-chart-view.component.scss'],
  standalone: true,
  imports: [CommonModule, GanttModule],
  providers: [ToolbarService, PdfExportService, ExcelExportService, SelectionService, DayMarkersService, ResizeService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GanttChartViewComponent {
  ganttData = new BehaviorSubject<Partial<GanttTaskData>[]>([]);
  taskSettings: object;
  eventMarkers: object[];
  ganttColumns: object[];
  toolbar: ToolbarItem[];
  tasks: Task[];
  timelineSettings: TimelineSettingsModel;
  taskFilter = new TaskFilterPipe(this.tasksService);
  labelSettings = { leftLabel: 'TaskName' };
  private tasks$: Observable<Task[]> = this.store.select(TaskState.getTasks).pipe(filter((tasks) => !!tasks));
  private _hasCompletedAtForPredecesssorsFeatureFlag = false;

  @Output() taskColumnClickEvent = new EventEmitter<{ id: number; task_list_id: number }>();

  @ViewChild('gantt') private gantt!: GanttComponent;
  splitterSettings = {
    columnIndex: 3,
  };

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly tasksService: LegacyTasksTabService,
    private readonly _matDialog: MatDialog,
    private readonly store: Store,
    private _accountService: AccountService,
  ) {
    this.setupGanttChart();
    const account = this._accountService.getCurrentAccount();
    this._hasCompletedAtForPredecesssorsFeatureFlag = account.features.includes(
      FeatureFlag.COMPLETED_AT_FOR_PREDECESSORS,
    );
  }

  ngAfterViewInit(): void {
    this.tasks$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tasks) => {
      this.tasks = tasks;
      this.fetchGanttTasks();
    });
  }

  private fetchGanttTasks(): void {
    const ganntTasks = this.tasks.map((task) => this.mapTaskToGantt(task));
    this.ganttData.next([...ganntTasks]);
  }

  private mapTaskToGantt(task: Task): Partial<GanttTaskData> {
    switch (task.task_type) {
      case 'section':
        return this.mapSectionToGantt(task);
      case 'key_date':
        return this.mapKeyDateToGantt(task);
      default:
        return this.mapRegularTaskToGantt(task);
    }
  }

  private mapSectionToGantt(task: Task): any {
    let ganttSection = {
      TaskID: task.id,
      TaskListId: task.task_list_id,
      TaskName: task.title,
      subtasks: [],
      StartDate: null,
      EndDate: null,
    };

    if (task.children?.length > 0) {
      let dates = [];
      let ganttSubTasks = [];
      for (let child_task of task.children) {
        const ganntChildTask = this.mapTaskToGantt(child_task);
        if (ganntChildTask?.StartDate) {
          dates.push(ganntChildTask.StartDate);
        }
        if (ganntChildTask?.EndDate) {
          dates.push(ganntChildTask.EndDate);
        }
        ganttSubTasks.push(ganntChildTask);
      }
      ganttSection.subtasks.push(...ganttSubTasks);

      if (dates.length > 0) {
        const start_date = new Date(Math.min.apply(null, dates));
        const end_date = new Date(Math.max.apply(null, dates));
        ganttSection.StartDate = start_date;
        ganttSection.EndDate = end_date;
      }
    }
    return ganttSection;
  }

  private mapKeyDateToGantt(task: Task): any {
    let ganttKeyDate = {
      TaskID: task.id,
      TaskName: task.title,
      TaskListId: task.task_list_id,
      StartDate: task.start_date ? new Date(task.start_date) : new Date(),
      Duration: 0,
    };
    return ganttKeyDate;
  }

  private mapRegularTaskToGantt(task: Task): GanttTaskData {
    let end_date = task.due_date ? moment(task.due_date).toDate() : new Date();

    if (this._hasCompletedAtForPredecesssorsFeatureFlag) {
      end_date = task.completed_at
        ? moment(task.completed_at).toDate()
        : task.due_date
          ? moment(task.due_date).toDate()
          : new Date();
    }

    let start_date = task.start_date ? moment(task.start_date).toDate() : end_date ? end_date : new Date();

    let ganttTask: GanttTaskData = {
      TaskID: task.id,
      TaskListId: task.task_list_id,
      TaskName: task.title,
      StartDate: start_date,
      EndDate: end_date,
      Predecessor: task.predecessor_codes,
      Status: task?.workflow_state,
      subtasks: [],
    };

    if (task.children?.length > 0) {
      for (let child_task of task.children) {
        ganttTask.subtasks.push(this.mapRegularTaskToGantt(child_task));
      }
    }

    if (task.start_date === task.due_date) ganttTask.Duration = 0;

    return ganttTask;
  }

  private setupGanttChart(): void {
    this.taskSettings = {
      id: 'TaskID',
      name: 'TaskName',
      TaskListId: 'TaskListId',
      startDate: 'StartDate',
      endDate: 'EndDate',
      duration: 'Duration',
      progress: 'Progress',
      dependency: 'Predecessor',
      child: 'subtasks',
    };

    this.eventMarkers = [
      {
        day: new Date(),
      },
    ];

    this.ganttColumns = [
      { field: 'TaskName', headerText: 'Name', textAlign: 'Left', width: '400', minWidth: 200, maxWidth: 500 },
      { field: 'StartDate', headerText: 'Start Date', width: '120', maxWidth: '120' },
      { field: 'EndDate', headerText: 'Due Date', width: '120', maxWidth: '120' },
    ];

    this.toolbar = ['ZoomIn', 'ZoomOut', 'ZoomToFit', 'PdfExport', 'ExcelExport'];

    this.timelineSettings = {
      topTier: {
        format: 'MMM',
        unit: 'Month',
      },
      bottomTier: {
        format: 'dd',
        unit: 'Day',
        count: 2,
      },
      timelineViewMode: 'Month',
      updateTimescaleView: true,
    };
  }

  private handleMilestoneCss(taskbarElement: HTMLDivElement, cssClasses: GanttElementCssClass, parent = false): void {
    const parentCssPart = parent ? '-parent' : '';
    let milestoneCssClass = `.e-gantt${parentCssPart}-milestone`;

    if (taskbarElement.querySelector(milestoneCssClass)) {
      const [topDiv, bottomDiv]: HTMLDivElement[] = [
        taskbarElement.querySelector(`${milestoneCssClass} .e${parentCssPart}-milestone-top`),
        taskbarElement.querySelector(`${milestoneCssClass} .e${parentCssPart}-milestone-bottom`),
      ];

      topDiv?.classList.add(cssClasses.forBorder);
      bottomDiv?.classList.add(cssClasses.forBorder);
    }
  }

  public rowSelected(event: RowSelectEventArgs): void {
    const eventData = event.data.taskData as GanttTaskData;
    this.taskColumnClickEvent.next({ id: eventData.TaskID, task_list_id: eventData.TaskListId });
  }

  public recordDoubleClick(event: RecordDoubleClickEventArgs): void {
    const eventData = event.rowData as GanttTaskData;
    this.taskColumnClickEvent.next({ id: eventData.TaskID, task_list_id: eventData.TaskListId });
  }

  public ganttToolbarClick(event: { item: { properties: { id: string } } }): void {
    if (event.item.properties.id.endsWith('pdfexport')) {
      this._matDialog
        .open(GanttPdfExportComponent, {
          panelClass: 'mailbox-compose-dialog',
          data: {},
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((result) => {
          if (result?.paper_size && result?.page_orientation && result?.fit_to_width) {
            const exportProperties: PdfExportProperties = {
              fileName: `gantt_${Date.now()}.pdf`,
              pageSize: result.paper_size,
              pageOrientation: result.page_orientation,
              fitToWidthSettings: {
                isFitToWidth: result?.fit_to_width == 'yes' ? true : false,
              },
            };
            this.gantt.pdfExport(exportProperties);
          }
        });
    }
    if (event.item.properties.id.endsWith('excelexport')) {
      const fileName = `gantt_${Date.now()}.xlsx`;
      this.gantt.excelExport({ fileName });
    }
  }

  public queryTaskbarInfo(event: {
    data: { taskData: GanttTaskData };
    taskbarBgColor: string;
    taskbarBorderColor: string;
    taskbarElement: HTMLDivElement;
  }): void {
    let taskbarElement;

    if ((taskbarElement = event?.taskbarElement)) {
      this.ganttData
        .asObservable()
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map((ganttData) => ganttData.find(({ TaskID }) => TaskID === event.data?.taskData?.TaskID)),
        )
        .subscribe((foundTask) => {
          if (!foundTask) return;

          let cssClasses: GanttElementCssClass;

          switch (foundTask.Status) {
            case 'accepted':
            case 'complete':
            case 'competitive':
              cssClasses = { forBackground: 'bg-green', forBorder: 'task-gantt-border-green' };
              break;
            case 'quoted':
              cssClasses = { forBackground: 'bg-yellow', forBorder: 'task-gantt-border-yellow' };
              break;
            case 'n_a':
            case 'sent':
              cssClasses = { forBackground: 'bg-gray', forBorder: 'task-gantt-border-gray' };
              break;
            case 'not_started':
              cssClasses = { forBackground: 'bg-gray-700', forBorder: 'task-gantt-border-gray-700' };
              break;
            case 'in_progress':
            case 'received':
              cssClasses = { forBackground: 'bg-blue', forBorder: 'task-gantt-border-blue' };
              break;
            case 'needs_review':
            case 'rejected':
              cssClasses = { forBackground: 'bg-red', forBorder: 'task-gantt-border-red' };
              break;
            default:
              return;
          }

          const innerTaskElement =
            taskbarElement.querySelector('.e-gantt-child-taskbar-inner-div') ||
            taskbarElement.querySelector('.e-gantt-parent-taskbar-inner-div');

          if (innerTaskElement) {
            innerTaskElement.classList.add(cssClasses.forBackground);
            return;
          }

          this.handleMilestoneCss(taskbarElement, cssClasses);
          this.handleMilestoneCss(taskbarElement, cssClasses, true);
        });
    }
  }

  public beforePdfExport(event): void {
    const ganttObject: GanttComponent = event.ganttObject;
    const startDateColumn = ganttObject.treeGrid.columns[1] as any;
    const endDateColumn = ganttObject.treeGrid.columns[2] as any;
    //startDateColumn.visible = false;
    //endDateColumn.visible = false;
  }
}
